<template>
  <div class="flex items-center mx-auto">
    <div class="flex-1">
      <vetster-input-group
        class="flex-1"
        :style="cssProps"
      >
        <div
          :id="geocoderId"
          class="mapbox w-full relative"
        >
          <div class="absolute z-99 top-1 leading-5 left-4 text-sm text-neutral-700">
            {{ $t('search.city') }}
          </div>
        </div>
      </vetster-input-group>
    </div>
  </div>
</template>

<script>
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import find from 'lodash/find';
import replace from 'lodash/replace';
import { mapGetters } from 'vuex';

import VetsterInputGroup from '@/components/VetsterInputGroup';
import { uuid } from '@/helpers/uuid';

export default {
  name: 'VetsterCityTypeahead',

  components: { VetsterInputGroup },

  props: {
    countries: {
      type: String,
      default: 'us',
    },
    geo: {
      type: Object,
      default: null,
    },
    locale: {
      type: String,
      default: 'en-US',
    },
  },

  data() {
    return {
      loading: false,
      key: import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN,
      geocoderId: `geocoder-${uuid()}`,
      inputs: {
        city: '',
        province: '',
        country: '',
      },
    };
  },

  computed: {
    ...mapGetters('search', [
      'displayCity',
      'getInputs',
      'getTempDisplayCity',
      'hasCity',
      'hasCountry',
    ]),
    ...mapGetters(['isGlobalUser']),

    cssProps() {
      const cssProperties = {};

      cssProperties['--padding'] = '0.5rem';
      cssProperties['--icon-margin'] = '4px';
      cssProperties['--icon-margin-close'] = '4px';
      cssProperties['--icon-top'] = '24px';
      cssProperties['--font-size'] = '18px';
      cssProperties['--font-size-suggestions'] = '0.9rem';
      cssProperties['--font-size-suggestions-desktop'] = '0.9rem';
      cssProperties['--icon-size'] = '16px';
      cssProperties['--icon-size-close'] = '16px';
      cssProperties['--icon-size-loading'] = '24px';
      cssProperties['--icon-size-search'] = '24px';

      return cssProperties;
    },

    placeholder() {
      return this.getTempDisplayCity || this.displayCity;
    },
  },

  beforeDestroy() {
    this.geocoder.off('result', this.updateAddress);
    this.getSearchInput()?.removeEventListener('blur', this.setPlaceholder);
    this.getSearchInput()?.removeEventListener('click', this.clearInput);
  },

  mounted() {
    this.geocoder = this.geo;

    if (!this.geocoder) {
      this.geocoder = new MapboxGeocoder({
        countries: this.isGlobalUser ? null : this.countries,
        accessToken: this.key,
        language: this.locale,
        placeholder: ' ',
        types: 'place',
        minLength: 3,
        autocomplete: true,
      });
    }

    this.targetSearch();
    this.geocoder.addTo(`#${this.geocoderId}`);
    this.geocoder.on('result', this.updateAddress);
    this.$nextTick(() => {
      if (this.hasCity || (this.isGlobalUser && this.hasCountry)) {
        this.inputs = {
          city: this.getInputs?.city,
          province: this.getInputs?.province,
          country: this.getInputs?.country,
        };
        const el = this.getSearchInput();

        if (el) {
          el.value = this.displayCity;
        }
      }
      this.getSearchInput()?.addEventListener('blur', this.setPlaceholder);
      this.getSearchInput()?.addEventListener('click', this.clearInput);
    });
  },

  methods: {
    getSearchInput() {
      return document.getElementsByClassName('mapboxgl-ctrl-geocoder--input')[0];
    },

    targetSearch() {
      if (!this.isGlobalUser) {
        this.geocoder.setCountries(this.countries);
      }
    },

    async updateAddress(data) {
      const { result } = data;

      // this.geocoder.setInput(
      //   this.getCity(result) + ', ' + this.getProvince(result)
      // );

      this.inputs = {
        city: this.getCity(result),
        province: this.getProvince(result),
        country: this.getCountry(result),
      };

      this.$emit('selected');
      this.$emit('update', this.inputs);
      this.getSearchInput().value = this.placeholder;
    },

    getCity(data) {
      // text_fr-ca
      return data?.text;
    },

    getProvince(data) {
      let provinceData = find(data.context, (item) => item.id.includes('region'));

      if (!provinceData?.short_code) {
        provinceData = find(data.context, (item) => item.id.includes('place'));
      }

      // Short code is returned CA-ON for Canada, US-PA for USA, GB-ENG for UK
      return provinceData && replace(provinceData.short_code, /[A-Z]{2}-/g, '');
    },

    getCountry(data) {
      const countryData = find(data.context, (item) => item.id.includes('country'));

      return countryData?.short_code;
    },

    clearInput() {
      this.geocoder.clear();
      document.getElementsByClassName('suggestions')[0].style.display = 'none';
      this.inputs = {
        city: '',
        province: '',
        country: '',
      };
    },

    setPlaceholder(el) {
      el.target.value = this.placeholder;
    },
  },
};
</script>

<style>
/* ------------------------------------ */
/* purgecss start ignore */
/* ------------------------------------ */
/* Basics */
.mapbox .mapboxgl-ctrl-geocoder,
.mapbox .mapboxgl-ctrl-geocoder *,
.mapbox .mapboxgl-ctrl-geocoder *:after,
.mapbox .mapboxgl-ctrl-geocoder *:before {
  box-sizing: border-box;
}

.mapbox .mapboxgl-ctrl-geocoder {
  line-height: 24px;
  background-color: var(--search-background-color, #FAFAFA);
  width: 100%;
  min-width: 240px;
  z-index: 1;
  transition: width 0.25s, min-width 0.25s;
  border-radius: var(--search-box-border-radius, 0);
}

@media (min-width: 1024px) {
  .mapbox .mapboxgl-ctrl-geocoder {
    min-width: var(--search-min-width, 240px);
  }
}

.mapbox .mapboxgl-ctrl-geocoder--input {
  @apply block w-full px-4 transition font-normal rounded-lg text-neutral-900 h-12 bg-white;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  border-color: #E5E5E5;
  border-width: 1px;
  padding-top: 26px;
  padding-right: 45px;
  padding-left: -10px;
  font-size: 16px;
  line-height: 1.5;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

.mapbox .mapboxgl-ctrl-geocoder--input::placeholder {
  @apply font-normal;
  color: var(--search-placeholder-color, theme('colors.gray.500'));
}

.mapbox .mapboxgl-ctrl-geocoder--input::-ms-clear {
  display: none; /* hide input clear button in IE */
}

.mapbox .mapboxgl-ctrl-geocoder--input:focus {
  @apply outline-none;
  --tw-ring-color: var(--search-border-color, theme('colors.ocean.100'));
  border-color: var(--search-border-color, theme('colors.ocean.700'));
  color: var(--search-color, theme('colors.gray.800'));
}

.mapbox .mapboxgl-ctrl-geocoder .mapboxgl-ctrl-geocoder--pin-right > * {
  @apply top-2 right-2;
  z-index: 2;
  position: absolute;
  display: none;
}

.mapbox .mapboxgl-ctrl-geocoder .mapboxgl-ctrl-geocoder--pin-right:focus,
.mapbox .mapboxgl-ctrl-geocoder--pin-right:active {
  @apply border-none;
}

/* Collapsed */
.mapbox .mapboxgl-ctrl-geocoder.mapboxgl-ctrl-geocoder--collapsed {
  width: 50px;
  min-width: 50px;
  transition: width 0.25s, min-width 0.25s;
}

/* Suggestions */
.mapbox .mapboxgl-ctrl-geocoder .suggestions {
  @apply rounded-lg mt-1 bg-white;
  box-shadow: 0px 0px 4px 0px #00000040;
  left: 0;
  list-style: none;
  position: absolute;
  width: 100%;
  z-index: 1000;
  overflow: hidden;
  font-size: var(--font-size-suggestions-desktop);
}

.mapbox .mapboxgl-ctrl-bottom-left .suggestions,
.mapbox .mapboxgl-ctrl-bottom-right .suggestions {
  top: auto;
  bottom: 100%;
}

.mapbox .mapboxgl-ctrl-geocoder .suggestions > li > a {
  @apply border-b cursor-pointer block;
  padding: 6px 12px;
  color: #404040;
}

.mapbox .mapboxgl-ctrl-geocoder .suggestions > .active > a,
.mapbox .mapboxgl-ctrl-geocoder .suggestions > li > a:hover {
  @apply text-neutral-700 bg-neutral-100;
  text-decoration: none;
  cursor: pointer;
}

.mapbox .mapboxgl-ctrl-geocoder .suggestions > li:hover {
  @apply text-neutral-700 bg-neutral-100 cursor-pointer;
  text-decoration: none;
}

.mapbox .mapboxgl-ctrl-geocoder--suggestion {
  @apply relative pl-10 my-2 mx-1;
}

.mapbox .mapboxgl-ctrl-geocoder--suggestion::before  {
  @apply absolute top-1 -left-0;
  content: url("data:image/svg+xml,%0A%3Csvg width='19' height='20' viewBox='0 0 15 21' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10.625 8C10.625 9.75781 9.21875 11.125 7.5 11.125C5.74219 11.125 4.375 9.75781 4.375 8C4.375 6.28125 5.74219 4.875 7.5 4.875C9.21875 4.875 10.625 6.28125 10.625 8ZM7.5 9.875C8.51562 9.875 9.375 9.05469 9.375 8C9.375 6.98438 8.51562 6.125 7.5 6.125C6.44531 6.125 5.625 6.98438 5.625 8C5.625 9.05469 6.44531 9.875 7.5 9.875ZM15 8C15 11.4375 10.4297 17.4922 8.39844 20.0312C7.92969 20.6172 7.03125 20.6172 6.5625 20.0312C4.53125 17.4922 0 11.4375 0 8C0 3.85938 3.32031 0.5 7.5 0.5C11.6406 0.5 15 3.85938 15 8ZM7.5 1.75C4.02344 1.75 1.25 4.5625 1.25 8C1.25 8.625 1.44531 9.44531 1.875 10.5C2.30469 11.5156 2.89062 12.6094 3.59375 13.7031C4.92188 15.8516 6.48438 17.9219 7.5 19.1719C8.47656 17.9219 10.0391 15.8516 11.3672 13.7031C12.0703 12.6094 12.6562 11.5156 13.0859 10.5C13.5156 9.44531 13.75 8.625 13.75 8C13.75 4.5625 10.9375 1.75 7.5 1.75Z' fill='%23171717'/%3E%3C/svg%3E%0A");
}

.mapbox .mapboxgl-ctrl-geocoder--suggestion::after  {
  @apply absolute top-1 right-0;
  content: url("data:image/svg+xml,%0A%3Csvg width='9' height='12' viewBox='0 0 9 14' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2.47656 0.4375L8.0625 6.375C8.21875 6.57031 8.33594 6.80469 8.33594 7C8.33594 7.23438 8.21875 7.46875 8.0625 7.66406L2.47656 13.6016C2.125 13.9922 1.53906 13.9922 1.14844 13.6406C0.757812 13.2891 0.757812 12.7031 1.10938 12.3125L6.10938 7L1.10938 1.72656C0.757812 1.33594 0.757812 0.75 1.14844 0.398438C1.53906 0.046875 2.125 0.046875 2.47656 0.4375Z' fill='%23404040'/%3E%3C/svg%3E%0A");
}

.mapbox .mapboxgl-ctrl-geocoder--suggestion-title {
  @apply text-neutral-900 text-sm;
}

.mapbox .mapboxgl-ctrl-geocoder--suggestion-address {
  @apply text-neutral-700 font-light text-sm mr-4;
}

.mapbox .mapboxgl-ctrl-geocoder--suggestion-title,
.mapbox .mapboxgl-ctrl-geocoder--suggestion-address {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

/* Icons */
.mapbox .mapboxgl-ctrl-geocoder--icon-search {
  @apply right-4 top-3;
  position: absolute;
  margin: 0;
  width: var(--icon-size-search);
  height: var(--icon-size-search);
  display: var(--search-icon-diplay, 'inline-block');
  fill: #171717;
}

.mapbox .mapboxgl-ctrl-geocoder--icon-close {
  width: var(--icon-size-close);
  height: var(--icon-size-close);
  fill: #171717;
}

.mapbox .mapboxgl-ctrl-geocoder--icon-loading {
  @apply bg-white;
  top: 8px !important;
  right: 16px !important;
  width: var(--icon-size-loading);
  height: var(--icon-size-loading);
  -moz-animation: rotate 0.8s infinite cubic-bezier(0.45, 0.05, 0.55, 0.95);
  -webkit-animation: rotate 0.8s infinite cubic-bezier(0.45, 0.05, 0.55, 0.95);
  animation: rotate 0.8s infinite cubic-bezier(0.45, 0.05, 0.55, 0.95);
  fill: #171717;
}

.mapbox .mapboxgl-ctrl-geocoder--button {
  z-index: 99999;
  padding: 0;
  margin: 0;
  border: none;
  cursor: pointer;
  height: 32px;
  width: 32px;
  background: #fff;
  font-size: 10px;
  line-height: 1;
}

.mapbox .mapboxgl-ctrl-geocoder--button:hover .mapboxgl-ctrl-geocoder--icon-close {
  fill: #909090;
  font-size: 2px;
}

.mapbox .mapbox-gl-geocoder--no-results {
  @apply p-4;
}

.mapbox .mapboxgl-ctrl-geocoder--powered-by {
  display: none !important;
}

/* Animation */
@-webkit-keyframes rotate {
  from {
    -webkit-transform: rotate(0);
    transform: rotate(0);
  }
  to {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}

@keyframes rotate {
  from {
    -webkit-transform: rotate(0);
    transform: rotate(0);
  }
  to {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}

/* Media queries*/
@media screen and (min-width: 640px) {
  .mapbox .mapboxgl-ctrl-geocoder.mapboxgl-ctrl-geocoder--collapsed {
    width: 36px;
    min-width: 36px;
  }

  .mapbox .mapboxgl-ctrl-geocoder {
    /*width: 33.3333%;*/
    line-height: 20px;
    /*max-width: 360px;*/
  }

  .mapbox .mapboxgl-ctrl-geocoder .suggestions {
    font-size: var(--font-size-suggestions-desktop);
  }

  .mapbox .mapbox-gl-geocoder--error {
    color: #909090;
    padding: 6px 12px;
    font-size: 14px;
    text-align: center;
  }
}
/* ------------------------------------ */
/* purgecss end ignore */
/* ------------------------------------ */
</style>
